const DEFAULT_CATEGORY_ALIASES = {
  analytics: ['analytics', 'statistics', 'statistic', 'stats', 'performance', 'measurement'],
  marketing: ['marketing', 'advertising', 'advertisement', 'ads', 'ad', 'targeting'],
  preferences: ['preferences', 'preference', 'functional', 'functionality'],
  necessary: ['necessary', 'essential', 'required', 'strictly_necessary'],
}

const DEFAULT_GRANTED_VALUES = ['true', '1', 'yes', 'y', 'accept', 'accepted', 'allow', 'allowed', 'granted', 'on', 'all']
const DEFAULT_DENIED_VALUES = ['false', '0', 'no', 'n', 'reject', 'rejected', 'deny', 'denied', 'decline', 'declined', 'off', 'none', 'necessary']
const DEFAULT_CONSENT_COOKIE_NAMES = [
  'CookieConsent',
  'CookieConsentPolicy',
  'OptanonConsent',
  'CookieScriptConsent',
  'CookieLawInfoConsent',
  'CookieLawInfoCheckbox',
  'cookie_consent',
  'cookieConsent',
  'cookieyes-consent',
  'cc_cookie',
  'klaro',
]
const DEFAULT_GROUP_IDS = {
  analytics: ['C0002'],
  marketing: ['C0004'],
  preferences: ['C0003'],
}

const decodeValue = value => {
  if (!value) return ''
  try {
    return decodeURIComponent(value)
  } catch {
    return value
  }
}

const normalizeToken = value =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[-\s]+/g, '_')

const normalizeList = values => {
  if (!values) return []
  if (Array.isArray(values)) return values.map(normalizeToken).filter(Boolean)
  return String(values)
    .split(',')
    .map(normalizeToken)
    .filter(Boolean)
}

const getCookieMap = () =>
  document.cookie.split(';').reduce((cookies, item) => {
    const separatorIndex = item.indexOf('=')
    if (separatorIndex === -1) return cookies

    const name = item.slice(0, separatorIndex).trim()
    const value = item.slice(separatorIndex + 1).trim()

    if (name) cookies[name] = decodeValue(value)
    return cookies
  }, {})

const parseJsonLike = value => {
  const candidates = [
    value,
    value.replace(/'/g, '"'),
    value.replace(/([{,]\s*)([a-zA-Z0-9_-]+)\s*:/g, '$1"$2":'),
  ]

  for (const candidate of candidates) {
    try {
      return JSON.parse(candidate)
    } catch {
      // continue
    }
  }

  return null
}

const readNestedValue = (source, keys) => {
  if (!source || typeof source !== 'object') return undefined

  for (const key of keys) {
    if (Object.prototype.hasOwnProperty.call(source, key)) return source[key]
    const matchedKey = Object.keys(source).find(item => normalizeToken(item) === normalizeToken(key))
    if (matchedKey) return source[matchedKey]
  }

  return undefined
}

const evaluateValue = (value, grantedValues, deniedValues) => {
  if (typeof value === 'boolean') return value
  if (typeof value === 'number') return value === 1

  const normalized = normalizeToken(value)
  if (grantedValues.includes(normalized)) return true
  if (deniedValues.includes(normalized)) return false

  return undefined
}

const arrayIncludesCategory = (items, aliases) =>
  Array.isArray(items) && items.some(item => aliases.includes(normalizeToken(item)))

const evaluateStructuredConsent = (parsed, aliases, grantedValues, deniedValues) => {
  if (!parsed || typeof parsed !== 'object') return undefined

  const directValue = readNestedValue(parsed, aliases)
  const directResult = evaluateValue(directValue, grantedValues, deniedValues)
  if (directResult !== undefined) return directResult

  const categories = readNestedValue(parsed, ['categories', 'acceptedCategories', 'accepted_categories', 'level', 'levels'])
  if (arrayIncludesCategory(categories, aliases)) return true

  const accepted = readNestedValue(parsed, ['accepted', 'enabled', 'granted', 'allow', 'allowed'])
  if (arrayIncludesCategory(accepted, aliases)) return true

  const rejected = readNestedValue(parsed, ['rejected', 'denied', 'disabled'])
  if (arrayIncludesCategory(rejected, aliases)) return false

  const consent = readNestedValue(parsed, ['consent', 'cookies', 'services'])
  if (consent && typeof consent === 'object') {
    const nestedValue = readNestedValue(consent, aliases)
    const nestedResult = evaluateValue(nestedValue, grantedValues, deniedValues)
    if (nestedResult !== undefined) return nestedResult
  }

  return undefined
}

const evaluateGroups = (value, groupIds) => {
  if (!groupIds.length) return undefined

  const normalizedValue = value.replace(/%2C/gi, ',').replace(/%3A/gi, ':').toLowerCase()

  for (const groupId of groupIds) {
    const escapedGroup = groupId.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    const match = normalizedValue.match(new RegExp(`(?:^|[,&;])${escapedGroup}:([01])`, 'i'))
    if (match) return match[1] === '1'
  }

  return undefined
}

const evaluateTextConsent = (value, aliases, grantedValues, deniedValues) => {
  const normalizedValue = normalizeToken(value)
  const wholeValueResult = evaluateValue(normalizedValue, grantedValues, deniedValues)
  if (wholeValueResult !== undefined) return wholeValueResult

  for (const alias of aliases) {
    const escapedAlias = alias.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    const match = normalizedValue.match(new RegExp(`(?:^|[,_;&\\s{]"?)${escapedAlias}"?[:=]([^,_;&\\s}]+)`, 'i'))
    if (match) {
      const result = evaluateValue(match[1], grantedValues, deniedValues)
      if (result !== undefined) return result
    }
  }

  return undefined
}

const buildConfig = rawConfig => {
  const config = rawConfig || {}

  return {
    required: config.required !== false,
    cookieNames: normalizeList(config.cookieNames).concat(normalizeList(DEFAULT_CONSENT_COOKIE_NAMES)),
    categoryCookieNames: {
      analytics: normalizeList(config.analyticsCookieNames),
      marketing: normalizeList(config.marketingCookieNames),
      preferences: normalizeList(config.preferencesCookieNames),
    },
    aliases: {
      analytics: normalizeList(config.analyticsAliases).concat(DEFAULT_CATEGORY_ALIASES.analytics),
      marketing: normalizeList(config.marketingAliases).concat(DEFAULT_CATEGORY_ALIASES.marketing),
      preferences: normalizeList(config.preferencesAliases).concat(DEFAULT_CATEGORY_ALIASES.preferences),
      necessary: DEFAULT_CATEGORY_ALIASES.necessary,
    },
    groupIds: {
      analytics: normalizeList(config.analyticsGroupIds).concat(DEFAULT_GROUP_IDS.analytics.map(normalizeToken)),
      marketing: normalizeList(config.marketingGroupIds).concat(DEFAULT_GROUP_IDS.marketing.map(normalizeToken)),
      preferences: normalizeList(config.preferencesGroupIds).concat(DEFAULT_GROUP_IDS.preferences.map(normalizeToken)),
    },
    grantedValues: normalizeList(config.grantedValues).concat(DEFAULT_GRANTED_VALUES),
    deniedValues: normalizeList(config.deniedValues).concat(DEFAULT_DENIED_VALUES),
  }
}

export const createCookieConsent = rawConfig => {
  const config = buildConfig(rawConfig)
  const listeners = []

  const getState = () => {
    if (!config.required) {
      return {
        explicit: true,
        source: 'config:bypass',
        necessary: true,
        analytics: true,
        marketing: true,
        preferences: true,
      }
    }

    const cookies = getCookieMap()
    const state = {
      explicit: false,
      source: null,
      necessary: true,
      analytics: false,
      marketing: false,
      preferences: false,
    }

    Object.entries(cookies).forEach(([cookieName, value]) => {
      const normalizedCookieName = normalizeToken(cookieName)
      const shouldInspectCookie = config.cookieNames.length === 0 || config.cookieNames.includes(normalizedCookieName)

      ;['analytics', 'marketing', 'preferences'].forEach(category => {
        if (state[category]) return

        const categoryCookieMatched = config.categoryCookieNames[category].includes(normalizedCookieName)
        if (!shouldInspectCookie && !categoryCookieMatched) return

        const aliases = config.aliases[category]
        const parsed = parseJsonLike(value)
        const result =
          evaluateGroups(value, config.groupIds[category]) ??
          evaluateStructuredConsent(parsed, aliases, config.grantedValues, config.deniedValues) ??
          evaluateTextConsent(value, aliases, config.grantedValues, config.deniedValues)

        if (result !== undefined) {
          state.explicit = true
          state.source = cookieName
          state[category] = result
        }
      })
    })

    return state
  }

  const canUse = category => {
    const normalizedCategory = normalizeToken(category)
    if (!normalizedCategory || normalizedCategory === 'necessary') return true

    const canonicalCategory =
      Object.keys(config.aliases).find(key => config.aliases[key].includes(normalizedCategory)) ||
      normalizedCategory

    return getState()[canonicalCategory] === true
  }

  const notifyListeners = () => {
    listeners.slice().forEach(listener => listener(getState()))
  }

  window.addEventListener('focus', notifyListeners)
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) notifyListeners()
  })

  return {
    canUse,
    getState,
    onChange(callback) {
      if (typeof callback !== 'function') return () => {}
      listeners.push(callback)
      return () => {
        const index = listeners.indexOf(callback)
        if (index !== -1) listeners.splice(index, 1)
      }
    },
  }
}
